/*
 * File: ApptPageToolbar.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Observes the Appt Store for change in the data title and toggles the dialogs. Styles the Schedule toolbar that dispalys the date, calendar icon, refresh and filter icons.
 */
import React, { useContext } from "react";
import { observer } from "mobx-react";
import { Theme, withStyles } from "@material-ui/core/styles";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import FilterListIcon from "@material-ui/icons/FilterList";
import DateRangeIcon from "@material-ui/icons/DateRange";
import RefreshIcon from "@material-ui/icons/Refresh";
import ViewDayIcon from "@material-ui/icons/ViewDay";
import ViewListIcon from "@material-ui/icons/ViewList";
import UIStore from "../store/UIStore";
import ApptStore from "services/appointments/store/DataStore";
import { StoreContext, useStore } from "utils/useStore";
import Preferences from "services/preferences/Store";
import { createStyles, WithStyles } from "@material-ui/core";

const styles = (theme: Theme) =>
  createStyles({
    dateIcon: {
      marginLeft: theme.spacing(-1.5),
      marginBottom: theme.spacing(0.75)
    },
    icon: {
      color: "#666666"
    },
    iconDisabled: {
      color: "inherit"
    },
    filterButton: {
      marginRight: theme.spacing(-1.5)
    },
    apptToolBar: {
      flex: "0 0 auto"
    },
    title: {
      textAlign: "left",
      color: theme.palette.common.title,
      maxWidth: "80%",
      marginTop: theme.spacing(-0.5),
      marginLeft: theme.spacing(-0.25),
      flex: 1,
      fontWeight: 400
    }
  });

type Props = WithStyles<typeof styles>;

const ApptPageToolbar = ({ classes }: Props) => {
  const storeManager = useContext(StoreContext);
  const preferencesStore = useStore(Preferences);
  const apptStore = useStore(ApptStore);
  const uiStore = useStore(UIStore);

  if (storeManager === undefined) {
    throw new Error("ApptPageToolbar is missing StoreContext");
  }

  const doRefresh = () => {
    apptStore.reloadAppts(storeManager);
  };

  const {
    dateString,
    toggleFiltersDialog,
    toggleCalendarDialog,
    showDayView,
    toggleDayView
  } = uiStore;

  return (
    <div className={classes.apptToolBar}>
      <Toolbar>
        <IconButton onClick={toggleCalendarDialog} className={classes.dateIcon}>
          <DateRangeIcon className={classes.icon} />
        </IconButton>
        <Typography
          color="inherit"
          className={classes.title}
          onClick={toggleCalendarDialog}
          variant="h5"
        >
          {dateString}
        </Typography>
        <IconButton
          onClick={() => toggleDayView(preferencesStore)}
          color="inherit"
        >
          {showDayView ? (
            <ViewListIcon className={classes.icon} />
          ) : (
            <ViewDayIcon className={classes.icon} />
          )}
        </IconButton>
        <IconButton onClick={doRefresh} color="inherit">
          <RefreshIcon className={classes.icon} />
        </IconButton>
        <IconButton
          className={classes.filterButton}
          onClick={toggleFiltersDialog}
          color="inherit"
        >
          <FilterListIcon className={classes.icon} />
        </IconButton>
      </Toolbar>
    </div>
  );
};

export default withStyles(styles)(observer(ApptPageToolbar));
